import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { MenuItems, MenuService, MenuItem, OngoingRequestTrackerService } from '@core';
import {CookieService} from "ngx-cookie-service";
import {take} from "rxjs/operators";
import {ProblemInstanceService} from "../problem-instance/problem-instance.service";

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private readonly router = inject(Router);
    private readonly menuService = inject(MenuService);
    private readonly progressIndicatorService = inject(OngoingRequestTrackerService);
    private readonly cookieService = inject(CookieService);
    private readonly problemInstanceService = inject(ProblemInstanceService);

    private readonly menuItems: MenuItem[];
    private readonly currentPage$: BehaviorSubject<number>;
    private readonly nextButtonDisabled$: BehaviorSubject<boolean>;
    private readonly navigationDisabled$: BehaviorSubject<boolean>;
    private readonly resetButtonOnly$: BehaviorSubject<boolean>;
    private readonly saveAndResetButtons$: BehaviorSubject<boolean>;
    private readonly saveAndResetButtonsActive$: BehaviorSubject<boolean>;
    private readonly saveButtonClick$: Subject<void>;
    private readonly resetButtonClick$: Subject<void>;

    constructor() {
        this.currentPage$ = new BehaviorSubject(0);
        this.nextButtonDisabled$ = new BehaviorSubject(false);
        this.navigationDisabled$ = new BehaviorSubject(false);
        this.resetButtonOnly$ = new BehaviorSubject(false);
        this.saveAndResetButtons$ = new BehaviorSubject(false);
        this.saveAndResetButtonsActive$ = new BehaviorSubject(false);
        this.saveButtonClick$ = new Subject();
        this.resetButtonClick$ = new Subject();
        this.menuItems = this.menuService.getMenuItems(MenuItems.routePlanner.children.planner.children);

        this.progressIndicatorService.isInProgress().subscribe(isInProgress => {
            if (isInProgress) {
                this.disableAllNavigationControls();
            } else {
                this.enableAllNavigationControls();
            }
        });
    }

    public getMenuItems(): MenuItem[] {
        return this.menuItems;
    }

    public getCurrentPage(): Observable<number> {
        return this.currentPage$.asObservable();
    }

    public getTotalPageCount(): number {
        return this.menuItems.length;
    }

    public setPage(page: number): void {
        this.currentPage$.next(page);
    }

    public nextPage(): void {
        this.savePreviousPageCookie();
        const nextPage = Math.min(this.currentPage$.value + 1, this.menuItems.length - 1);
        this.currentPage$.next(nextPage);
    }

    public previousPage(): void {
        this.savePreviousPageCookie();
        const previousPage = Math.max(this.currentPage$.value - 1, 0);
        this.currentPage$.next(previousPage);
        this.resetButtonsState();
    }

    public navigate(page?: number): void {
        this.router.navigateByUrl(
            `${MenuItems.routePlanner.path}/${MenuItems.routePlanner.children.planner.path}/${
                this.menuItems[page ?? this.currentPage$.value].path
            }`
        );
    }

    public navigateNext(): void {
        this.savePreviousPageCookie();
        const nextPage = Math.min(this.currentPage$.value + 1, this.menuItems.length - 1);
        this.router.navigateByUrl(
            `${MenuItems.routePlanner.path}/${MenuItems.routePlanner.children.planner.path}/${this.menuItems[nextPage].path}`
        );
    }

    public navigateToStep1(): void {
        this.savePreviousPageCookie();
        this.setPage(0);
        this.navigate(0);
        this.problemInstanceService.getProblemInstance().subscribe(instanceResult => {
            instanceResult.problemInstance.pageId = "0";
        });
    }

    public disableNextButton(disable: boolean): void {
        this.nextButtonDisabled$.next(disable);
    }

    public nextButtonDisabled(): Observable<boolean> {
        return this.nextButtonDisabled$.asObservable();
    }

    public disableNavigation(disable: boolean): void {
        this.navigationDisabled$.next(disable);
    }

    public navigationDisabled(): Observable<boolean> {
        return this.navigationDisabled$.asObservable();
    }

    public showResetButtonOnly(show: boolean): void {
        this.resetButtonOnly$.next(show);
    }

    public resetButtonOnly(): Observable<boolean> {
        return this.resetButtonOnly$.asObservable();
    }

    public showSaveAndResetButtons(show: boolean): void {
        this.saveAndResetButtons$.next(show);
    }

    public saveAndResetButtons(): Observable<boolean> {
        return this.saveAndResetButtons$.asObservable();
    }

    public activateSaveAndResetButtons(active: boolean): void {
        this.saveAndResetButtonsActive$.next(active);
    }

    public saveAndResetButtonsActive(): Observable<boolean> {
        return this.saveAndResetButtonsActive$.asObservable();
    }

    public saveButtonClicked(): void {
        this.saveButtonClick$.next();
    }

    public saveButtonClick(): Observable<void> {
        return this.saveButtonClick$.asObservable();
    }

    public resetButtonClicked(): void {
        this.resetButtonClick$.next();
    }

    public resetButtonClick(): Observable<void> {
        return this.resetButtonClick$.asObservable();
    }

    public isCurrentPageRoutePlanning(): boolean {
        return (
            this.menuItems[this.currentPage$.value].id ===
            MenuItems.routePlanner.children.planner.children.routePlanning.id
        );
    }

    public isCurrentPageOptimization(): boolean {
        return (
            this.menuItems[this.currentPage$.value].id ===
            MenuItems.routePlanner.children.planner.children.optimization.id
        );
    }

    public navigateToPage(index: number): void {
        this.savePreviousPageCookie();
        this.setPage(index);
        this.resetButtonsState();
    }

    public isPrevStepPriorOptimization(): boolean {
        return +this.cookieService.get('previous-page') < 2;
    }

    private resetButtonsState(): void {
        this.disableNextButton(false);
    }

    private enableAllNavigationControls(): void {
        this.disableNextButton(false);
        this.disableNavigation(false);
        this.activateSaveAndResetButtons(true);
    }

    private disableAllNavigationControls(): void {
        this.disableNextButton(true);
        this.disableNavigation(true);
        this.activateSaveAndResetButtons(false);
    }

    private savePreviousPageCookie() {
        this.getCurrentPage().pipe(
            take(1)
        ).subscribe(currentPage => {
            this.cookieService.set('previous-page', currentPage.toString(),{
                expires: null, path: '/', sameSite: 'Strict' });
        });
    }
}
